/** Non-destructive visual noise for custom / textured blocks. */

export const BLOCK_NOISE_TYPES = [
  'omni',
  'gradientBottomTop',
  'gradientTopBottom',
  'wavesSmall',
  'wavesMedium',
  'wavesLarge',
] as const;

/** Legacy type id kept for reading older saved noise_params. */
const LEGACY_WAVES_TYPE = 'waves';

/**
 * Wave cycles across one block edge (normalized 0..1 space).
 * Wavelength therefore scales with the block, not voxel count.
 */
const WAVE_CYCLES: Record<
  'wavesSmall' | 'wavesMedium' | 'wavesLarge',
  number
> = {
  wavesSmall: 8,
  wavesMedium: 3,
  wavesLarge: 1.25,
};

export type BlockNoiseType = (typeof BLOCK_NOISE_TYPES)[number];

export const BLOCK_NOISE_BLEND_MODES = [
  'normal',
  'multiply',
  'screen',
  'overlay',
  'softLight',
  'add',
  'subtract',
  'darken',
  'lighten',
  'colorBurn',
  'colorDodge',
] as const;

export type BlockNoiseBlendMode = (typeof BLOCK_NOISE_BLEND_MODES)[number];

export interface BlockNoiseParams {
  type: BlockNoiseType;
  blend: BlockNoiseBlendMode;
  /** 0..100 */
  intensity: number;
  /**
   * Noise pattern seed. Inventory uses 0..100 (default 0 for new blocks).
   * Legacy rows may still store larger uint32 values.
   */
  seed?: number;
}

export const DEFAULT_BLOCK_NOISE: BlockNoiseParams = {
  type: 'omni',
  blend: 'overlay',
  intensity: 0,
  seed: 0,
};

/** Random uint32 seed for a new block draft. */
export function createNoiseSeed(): number {
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    return buf[0]!;
  }
  return (Math.random() * 0x100000000) >>> 0;
}

export function isValidNoiseSeed(value: unknown): value is number {
  return typeof value === 'number'
    && Number.isInteger(value)
    && value >= 0
    && value <= 0xffffffff;
}

/**
 * Seed used for sampling. Prefer stored `noise.seed`; fall back to block id for
 * legacy definitions that predate persistent seeds.
 */
export function resolveNoiseSeed(
  noise: BlockNoiseParams | null | undefined,
  fallbackId = 0,
): number {
  if (noise && isValidNoiseSeed(noise.seed)) return noise.seed >>> 0;
  return fallbackId >>> 0;
}

/** Ensure params carry a seed (`fallbackSeed` for legacy rows; otherwise 0). */
export function withNoiseSeed(
  noise: BlockNoiseParams,
  fallbackSeed?: number,
): BlockNoiseParams {
  if (isValidNoiseSeed(noise.seed)) {
    return { ...noise, seed: noise.seed >>> 0 };
  }
  const seed = fallbackSeed !== undefined ? fallbackSeed >>> 0 : 0;
  return { ...noise, seed };
}

export function isValidBlockNoiseType(value: unknown): value is BlockNoiseType {
  return typeof value === 'string' &&
    (BLOCK_NOISE_TYPES as readonly string[]).includes(value);
}

export function isValidBlockNoiseBlendMode(
  value: unknown,
): value is BlockNoiseBlendMode {
  return typeof value === 'string' &&
    (BLOCK_NOISE_BLEND_MODES as readonly string[]).includes(value);
}

/** Normalize API / DB JSON into params, or null when inactive / invalid. */
export function parseBlockNoise(raw: unknown): BlockNoiseParams | null {
  if (raw == null || raw === '') return null;
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const type = record.type === LEGACY_WAVES_TYPE ? 'wavesMedium' : record.type;
  if (!isValidBlockNoiseType(type)) return null;
  if (!isValidBlockNoiseBlendMode(record.blend)) return null;
  const intensity = Number(record.intensity);
  if (!Number.isFinite(intensity)) return null;
  const params: BlockNoiseParams = {
    type,
    blend: record.blend,
    intensity: Math.max(0, Math.min(100, Math.round(intensity))),
  };
  if (isValidNoiseSeed(record.seed)) params.seed = record.seed >>> 0;
  return params;
}

export function isNoiseActive(noise: BlockNoiseParams | null | undefined): boolean {
  return !!noise && noise.intensity > 0;
}

/** Deterministic white-noise sample in 0..1 for one micro-voxel. */
export function sampleWhiteNoise(
  x: number,
  y: number,
  z: number,
  gridSize: number,
  seed = 0,
): number {
  let h = (x * 374761393 + y * 668265263 + z * 2147483647) ^
    (gridSize * 1013904223) ^
    (seed * 1597334677);
  h = Math.imul(h ^ (h >>> 15), h | 1);
  h ^= h + Math.imul(h ^ (h >>> 7), h | 61);
  return ((h ^ (h >>> 14)) >>> 0) / 4294967296;
}

/** Spatial mask in 0..1 that shapes noise strength by type. */
export function noiseIntensityMask(
  type: BlockNoiseType,
  x: number,
  y: number,
  z: number,
  gridSize: number,
): number {
  const max = Math.max(1, gridSize - 1);
  const u = (x + 0.5) / gridSize;
  const v = (y + 0.5) / gridSize;
  const w = (z + 0.5) / gridSize;
  switch (type) {
    case 'omni':
      return 1;
    case 'gradientBottomTop':
      // Max at bottom (y=0), zero at top.
      return 1 - y / max;
    case 'gradientTopBottom':
      return y / max;
    case 'wavesSmall':
      return waveIntensityMask(u, v, w, WAVE_CYCLES.wavesSmall);
    case 'wavesMedium':
      return waveIntensityMask(u, v, w, WAVE_CYCLES.wavesMedium);
    case 'wavesLarge':
      return waveIntensityMask(u, v, w, WAVE_CYCLES.wavesLarge);
    default:
      return 1;
  }
}

/**
 * Diagonal band mask in block-normalized space.
 * `cycles` = how many full high→low bands fit across the block edge.
 */
function waveIntensityMask(
  u: number,
  v: number,
  w: number,
  cycles: number,
): number {
  const phase = 0.62 * u + 0.48 * w + 0.36 * v;
  const wave = Math.sin(phase * Math.PI * 2 * cycles);
  // Full 0..1 contrast so quiet bands vs noisy bands are obvious.
  return clamp01(0.5 + 0.5 * wave);
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function blendChannel(
  mode: BlockNoiseBlendMode,
  base: number,
  noise: number,
): number {
  switch (mode) {
    case 'normal':
      return noise;
    case 'multiply':
      return base * noise;
    case 'screen':
      return 1 - (1 - base) * (1 - noise);
    case 'overlay':
      return base < 0.5
        ? 2 * base * noise
        : 1 - 2 * (1 - base) * (1 - noise);
    case 'softLight': {
      if (noise < 0.5) {
        return base - (1 - 2 * noise) * base * (1 - base);
      }
      const d = base <= 0.25
        ? ((16 * base - 12) * base + 4) * base
        : Math.sqrt(base);
      return base + (2 * noise - 1) * (d - base);
    }
    case 'add':
      return base + noise;
    case 'subtract':
      return base - noise;
    case 'darken':
      return Math.min(base, noise);
    case 'lighten':
      return Math.max(base, noise);
    case 'colorBurn':
      if (noise <= 0) return 0;
      return 1 - (1 - base) / noise;
    case 'colorDodge':
      if (noise >= 1) return 1;
      return base / (1 - noise);
    default:
      return noise;
  }
}

/**
 * Apply noise to a linear RGB color (0..1). Alpha is unchanged by the caller.
 * `seed` should be `resolveNoiseSeed(noise, definition.id)`.
 */
export function applyBlockNoiseRgb(
  r: number,
  g: number,
  b: number,
  x: number,
  y: number,
  z: number,
  gridSize: number,
  noise: BlockNoiseParams,
  seed = 0,
): [number, number, number] {
  const amount = (noise.intensity / 100) *
    noiseIntensityMask(noise.type, x, y, z, gridSize);
  if (amount <= 0) return [r, g, b];
  const n = sampleWhiteNoise(x, y, z, gridSize, seed);
  return [
    clamp01(r + (blendChannel(noise.blend, r, n) - r) * amount),
    clamp01(g + (blendChannel(noise.blend, g, n) - g) * amount),
    clamp01(b + (blendChannel(noise.blend, b, n) - b) * amount),
  ];
}

/**
 * Apply noise to one textured-cube face (#rrggbb / #rrggbbaa).
 * Uses face resolution as the noise grid; `faceIndex` seeds the Z axis so
 * faces differ while wavelength still tracks face size.
 */
export function applyNoiseToTextureFace(
  face: readonly string[],
  faceIndex: number,
  noise: BlockNoiseParams,
  seed = 0,
): string[] {
  if (!isNoiseActive(noise) || face.length === 0) return [...face];
  const size = Math.round(Math.sqrt(face.length));
  if (size * size !== face.length) return [...face];
  return face.map((color, index) => {
    const x = index % size;
    const y = Math.floor(index / size);
    const [r0, g0, b0, a] = rgbaBytes(color);
    const [r, g, b] = applyBlockNoiseRgb(
      r0 / 255,
      g0 / 255,
      b0 / 255,
      x,
      y,
      faceIndex,
      size,
      noise,
      seed,
    );
    return `#${toByteHex(r)}${toByteHex(g)}${toByteHex(b)}${toByteHex(a / 255)}`;
  });
}

export function applyNoiseToTexturePixels(
  pixels: ReadonlyArray<readonly string[]>,
  noise: BlockNoiseParams | null | undefined,
  seed = 0,
): string[][] {
  if (!isNoiseActive(noise)) {
    return pixels.map((face) => [...face]);
  }
  return pixels.map((face, faceIndex) =>
    applyNoiseToTextureFace(face, faceIndex, noise!, seed));
}

function rgbaBytes(color: string): [number, number, number, number] {
  const hex = color.slice(1);
  const value = Number.parseInt(hex, 16);
  if (!Number.isFinite(value)) return [255, 255, 255, 255];
  return hex.length === 8
    ? [(value >>> 24) & 255, (value >>> 16) & 255, (value >>> 8) & 255, value & 255]
    : [(value >> 16) & 255, (value >> 8) & 255, value & 255, 255];
}

function toByteHex(unit: number): string {
  return Math.round(clamp01(unit) * 255).toString(16).padStart(2, '0');
}
